import { useEffect } from "react";

export const SITE = {
  name: "RAMA DÉCO",
  legalName: "RAMA DÉCO",
  slogan: "Construction & Rénovation",
  url: (process.env.REACT_APP_SITE_URL || "https://www.ramadeco.fr").replace(/\/$/, ""),
  phone: "+33627443230",
  phoneDisplay: "06 27 44 32 30",
  email: "contact@ramadeco.fr",
  street: "30B rue des Mésanges",
  postalCode: "93420",
  city: "Villepinte",
  region: "Île-de-France",
  country: "FR",
  lat: 48.9569,
  lng: 2.5347,
  hours: "Lundi — Samedi, 8h — 19h",
  siren: "824 763 015",
  siret: "824 763 015 000 24",
  priceRange: "€€",
  logo: "/brand/logo-full-bordeaux.png",
  ogImage: "/brand/og-cover.jpg",
};

export const abs = (p = "/") => (p.startsWith("http") ? p : `${SITE.url}${p.startsWith("/") ? p : `/${p}`}`);

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export const localBusinessLd = (areaServed = []) => ({
  "@context": "https://schema.org",
  "@type": ["GeneralContractor", "HomeAndConstructionBusiness", "LocalBusiness"],
  "@id": `${SITE.url}/#business`,
  name: SITE.name,
  legalName: SITE.legalName,
  description:
    "RAMA DÉCO, entreprise de travaux intérieurs, rénovation et second œuvre à Villepinte (93). Interventions à Paris, en Seine-Saint-Denis et dans toute l'Île-de-France.",
  url: SITE.url,
  telephone: SITE.phone,
  email: SITE.email,
  image: abs(SITE.ogImage),
  logo: abs(SITE.logo),
  priceRange: SITE.priceRange,
  currenciesAccepted: "EUR",
  paymentAccepted: "Virement, chèque, espèces",
  address: {
    "@type": "PostalAddress",
    streetAddress: SITE.street,
    postalCode: SITE.postalCode,
    addressLocality: SITE.city,
    addressRegion: SITE.region,
    addressCountry: SITE.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: SITE.lat, longitude: SITE.lng },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
      opens: "08:00",
      closes: "19:00",
    },
  ],
  identifier: [
    { "@type": "PropertyValue", name: "SIREN", value: SITE.siren },
    { "@type": "PropertyValue", name: "SIRET", value: SITE.siret },
  ],
  areaServed: areaServed.length
    ? areaServed.map((a) => ({ "@type": "Place", name: a }))
    : [{ "@type": "Place", name: "Île-de-France" }],
  knowsAbout: [
    "Rénovation intérieure",
    "Peinture intérieure",
    "Placo et cloisons",
    "Pose de sols et carrelage",
    "Rénovation de salle de bains",
    "Second œuvre bâtiment",
  ],
});

export const breadcrumbLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: abs(it.to),
  })),
});

export const faqLd = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceLd = ({ name, description, area, path }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  name,
  description,
  provider: { "@id": `${SITE.url}/#business` },
  areaServed: { "@type": "Place", name: area },
  url: abs(path),
  category: "Rénovation et travaux intérieurs",
  offers: {
    "@type": "Offer",
    availability: "https://schema.org/InStock",
    priceCurrency: "EUR",
    priceSpecification: {
      "@type": "PriceSpecification",
      description: "Devis gratuit et estimation en ligne immédiate",
    },
  },
});

export default function Seo({
  title,
  description,
  path = "/",
  image,
  jsonLd,
  noindex = false,
  type = "website",
}) {
  const canonical = abs(path);
  const img = abs(image || SITE.ogImage);
  const key = JSON.stringify([title, description, path, noindex, type, img, jsonLd]);

  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");
    upsertLink("canonical", canonical);
    upsertMeta("property", "og:site_name", SITE.name);
    upsertMeta("property", "og:locale", "fr_FR");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    document.head.querySelectorAll("script[data-seo-ld]").forEach((n) => n.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    blocks.filter(Boolean).forEach((b) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-ld", "1");
      s.textContent = JSON.stringify(b);
      document.head.appendChild(s);
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
